import type { BaseURL } from './types';

export interface AppBossIntegration {
  id: string;
  name: string;
  issuer: string;
  jwksUrl: string;
  authorityUrl: string;
  operationsUrl: string;
  status: 'enabled' | 'disabled';
  revision: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface AppBossIntegrationDraft {
  name: string;
  issuer: string;
  jwksUrl: string;
  authorityUrl: string;
  operationsUrl: string;
  status: 'enabled' | 'disabled';
  serviceCredential: string;
}

export const emptyIntegration: AppBossIntegrationDraft = {
  name: '', issuer: '', jwksUrl: '', authorityUrl: '', operationsUrl: '', status: 'disabled', serviceCredential: ''
};

export function publicIntegration(item: AppBossIntegration): AppBossIntegration {
  const { id, name, issuer, jwksUrl, authorityUrl, operationsUrl, status, revision, createdAt, updatedAt } = item;
  return { id, name, issuer, jwksUrl, authorityUrl, operationsUrl, status, revision, createdAt, updatedAt };
}

const integrationErrors: Record<string, string> = {
  pairing_expired: '配对码已过期，请重新生成。',
  pairing_invalid: '配对信息格式不正确，请检查名称和 AppBoss 服务地址。',
  pairing_conflict: '配对码已用于另一份连接请求，请核对 AppBoss 的连接状态。',
  pairing_integration_exists: '此 AppBoss 已有连接，不会覆盖原设置。',
  invalid_enrollment_request: '接入设置格式不正确，请重新加载后选择连接与能力。',
  enrollment_source_changed: '原入口已被修改，请重新加载并确认最新设置。旧入口未被本次接入操作修改。',
  enrollment_unsupported: '此入口的上游或路由暂不支持接入 AppBoss，目前仅支持已适配的 OpenAI HTTP 文字请求。',
  enrollment_scope_changed: '可用模型或操作已变化，请重新加载后选择。',
  enrollment_integration_unavailable: '所选 AppBoss 连接已停用或不可用，请检查连接后重新加载。',
  enrollment_conflict: '此入口的接入设置已变化，请重新加载查看已有的独立入口。不会自动覆盖。',
  source_not_legacy: '此入口已使用 AppBoss 令牌，请直接管理其设置。',
  source_disabled: '原入口未启用，请先检查原入口的状态。',
  protocol_unsupported: '此入口的协议暂不支持接入，目前仅支持 OpenAI HTTP 文字请求。',
  routing_unsupported: '此入口使用集群、模型路由、备用上游或 WebSocket，暂不支持自动接入。',
  compliance_unsupported: '此入口启用了价值观合规路由，暂不支持自动接入。',
  upstream_unavailable: '原入口的上游不存在或未启用，请先检查上游设置。',
  upstream_credential_missing: '此上游需要访问凭据，但尚未配置。请在“上游服务”中检查此上游的凭据，不要更换 AppBoss 连接凭据。',
  models_unavailable: '服务器尚未确认可用模型，请先检查上游与模型配置。',
  trust_unavailable: '没有已启用的 AppBoss 连接，请先完成连接配置。',
  adapter_unqualified: '此服务尚未支持 AppBoss 令牌调用。这是功能未接通，不是连接设置错误。现有 API Key 方式不受影响。',
  paid_access_disabled: 'AppBoss 付费调用尚未开放，暂不能启用。',
  service_credential_required: '启用前必须回填 AppBoss 生成的回调服务凭据。',
  credential_encryption_unavailable: '服务端凭据加密尚未配置，无法保存回调凭据。',
  invalid_integration_endpoint: 'AppBoss 连接地址格式不符合服务器要求。',
  invalid_service_credential: '回调服务凭据格式不符合服务器要求。',
  lifecycle_unavailable: '暂时无法读取入口状态和依赖，不能视为没有依赖。',
  invalid_lifecycle_request: '入口版本或备注格式不正确，请刷新后重试；备注可留空，最多 500 个字符。',
  invalid_lifecycle_action: '服务器不支持此入口状态操作。',
  invalid_force_action: '强制确认仅适用于删除入口。',
  lifecycle_impact_changed: '入口或依赖已变化，请刷新后重新核对影响范围。',
  entrance_has_dependencies: '此入口仍有已知依赖，请先解除绑定，或核对影响后明确确认强制删除。',
  entrance_not_found: '入口已不存在，请刷新入口列表。',
  dependency_registry_capacity: '依赖登记达到容量限制，不能忽略已有依赖。'
};
export function safeIntegrationErrorCode(code: unknown) {
  return typeof code === 'string' && Object.prototype.hasOwnProperty.call(integrationErrors, code) ? code : '';
}

export function integrationDraft(item: AppBossIntegration): AppBossIntegrationDraft {
  return { name: item.name, issuer: item.issuer, jwksUrl: item.jwksUrl, authorityUrl: item.authorityUrl, operationsUrl: item.operationsUrl, status: item.status, serviceCredential: '' };
}

export function validIntegrationEndpoint(raw: string) {
  try {
    if (/[\r\n]/.test(raw)) return false;
    const url = new URL(raw);
    return url.protocol === 'https:' && !!url.hostname && !url.username && !url.password && !url.search && !url.hash;
  } catch { return false; }
}

export function integrationValidation(draft: AppBossIntegrationDraft, existing?: AppBossIntegration | null) {
  if (!draft.name.trim() || new TextEncoder().encode(draft.name.trim()).length > 200) return '名称不能为空，且 UTF-8 长度不能超过 200 字节。';
  if (![draft.issuer, draft.jwksUrl, draft.authorityUrl, draft.operationsUrl].every(validIntegrationEndpoint)) return '所有连接地址必须为 HTTPS，且不含用户名、密码、查询参数或片段。';
  if (draft.serviceCredential && (draft.serviceCredential.length < 32 || draft.serviceCredential.length > 4096 || /[\r\n]/.test(draft.serviceCredential))) return '回调服务凭据须为 32 至 4096 个字符，不能包含换行。';
  if (!existing && draft.status !== 'disabled') return '首次保存时暂不启用连接；完成双方凭据配置后再启用。';
  return '';
}

export function integrationPayload(draft: AppBossIntegrationDraft, existing?: AppBossIntegration | null, rotate = false) {
  return {
    ...(existing ? { id: existing.id } : {}), name: draft.name.trim(), issuer: draft.issuer.trim(), jwksUrl: draft.jwksUrl.trim(),
    authorityUrl: draft.authorityUrl.trim(), operationsUrl: draft.operationsUrl.trim(), status: rotate ? 'disabled' : draft.status,
    expectedRevision: existing?.revision ?? 0,
    ...(draft.serviceCredential ? { serviceCredential: draft.serviceCredential } : {}),
    ...(rotate ? { rotateManagementCredential: true } : {})
  };
}

export function tokenEntranceDraft(draft: Partial<BaseURL>, existing?: BaseURL | null): Partial<BaseURL> {
  const status = existing?.authMode === 'appboss_token' ? existing.status : 'disabled';
  return { ...draft, authMode: 'appboss_token', status, appbossBillingEnabled: false, appbossBillingMode: 'gateway', appbossServiceId: '', appbossBalanceCheckUrl: '', appbossDeductUrl: '' };
}

export function validateTokenEntrance(draft: Partial<BaseURL>, existing?: BaseURL | null) {
  if (draft.authMode !== 'appboss_token') return '';
  if (existing && existing.authMode !== 'appboss_token') return '请使用“接入 AppBoss”创建独立入口，保留原入口与 API Key。';
  if (existing?.authMode === 'appboss_token') {
    if (draft.status !== existing.status) return '请在“启用与停用”中修改入口状态。';
  } else if (draft.status !== 'disabled') return '新 AppBoss 令牌入口保存后暂不启用，完成设置后再启用。';
  if (!draft.appbossIntegrationId) return '请选择已启用的 AppBoss 连接。';
  return '';
}

export function authenticationLabel(mode: BaseURL['authMode']) {
  return mode === 'appboss_token' ? 'AppBoss 令牌' : mode === 'allow_anonymous' ? '免密访问' : 'API Key';
}

export function appBossEndpoints(root: string) {
  if (!validIntegrationEndpoint(root)) return null;
  const parsed = new URL(root);
  if (parsed.pathname !== '/') return null;
  const base = parsed.origin;
  return { issuer: base, jwksUrl: base + '/.well-known/aigate-jwks.json', authorityUrl: base + '/integrations/aigate/v1/authority', operationsUrl: base + '/integrations/aigate/v1/operations' };
}

export function integrationError(error: unknown) {
  const status = (error as { status?: number })?.status;
  const code = (error as { code?: string })?.code;
  if (status === 401 || status === 403) return '会话已失效或当前账号没有权限，请重新登录。';
  if (safeIntegrationErrorCode(code)) return integrationErrors[code!];
  if (status === 409) return '配置版本已变化。请重新加载核对，不会自动覆盖。';
  if (status === 404) return '当前服务器尚未提供 AppBoss 连接接口。';
  return '请求未完成，当前状态未确认。请检查服务器后重试。';
}
